import { useState, useEffect, useRef, useMemo } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { CheckCircle, X, Clock, Package, Trash2, Plus, Minus, XCircle, Check, Truck, ChevronDown, FileText, Loader2, Pencil, MapPin, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, readPath } from "@/lib/queryClient";
import { orderTotals } from "@/lib/orderTotals";
import { formatDeliveryFeePrice, orderGetsDeliveryFee } from "@shared/deliveryFee";
import { useOrderActions } from "@/hooks/useOrderActions";
import { QbSyncedMark } from "@/components/qb-synced-mark";
import { OrderSourceMark } from "@/components/agent-order-bubble";
import { getFulfillmentDateOption, getStaffFulfillmentDates, type WindowConfig } from "@/lib/frontend-periods";
import type { OrderWithDetails } from "@shared/schema";
import { activeAliases } from "@shared/productAliases";
import { parseTypedPrice } from "@shared/linePrice";
import { accountMenuPrice, customerTypePath, type CustomerTypeDetail } from "@/lib/customerTypes";

interface OrderSlipProps {
  order: OrderWithDetails | null;
  isOpen: boolean;
  onClose: () => void;
  highlightProductId?: number | null;
  onSendToQuickBooks?: (orderId: number) => void;
  isSendingToQB?: boolean;
  /** When true: hides all editing controls and renders without the Dialog wrapper */
  readOnly?: boolean;
  /** When true: renders a product image thumbnail on each order item row. Only pass true
   *  from the demo admin view — see the hardcoded org slug comment at the usage site. */
  showItemThumbnails?: boolean;
  /** Called after an item is added, re-quantified or removed. The slip's own lists
   *  refetch only when it closes (`refetchType: 'none'` below); a page whose numbers
   *  sit beside the open slip — the procurement page — refetches its own here. */
  onItemsChanged?: () => void;
}

// Helper function to intelligently update notes based on quantity changes
const updateSmartNotes = (currentNotes: string, productName: string, newQuantity: number, originalQuantity: number): string => {
  if (!currentNotes) {
    // No existing notes - add removal or addition note
    if (newQuantity < originalQuantity) {
      const quantityReduced = originalQuantity - newQuantity;
      if (newQuantity === 0) {
        return `x${originalQuantity} ${productName} removed (out of stock)`;
      } else {
        return `x${quantityReduced} ${productName} removed (out of stock)`;
      }
    } else if (newQuantity > originalQuantity) {
      const quantityAdded = newQuantity - originalQuantity;
      return `x${quantityAdded} ${productName} added`;
    }
    return currentNotes;
  }

  // Parse existing notes into lines
  const lines = currentNotes.split('\n').filter(line => line.trim());
  const removalPattern = new RegExp(`^x(\\d+) ${productName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} removed \\(out of stock\\)$`);
  const additionPattern = new RegExp(`^x(\\d+) ${productName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} added$`);
  
  // Find existing removal and addition notes for this product
  let existingRemovalLineIndex = -1;
  let existingAdditionLineIndex = -1;
  let existingRemovalQuantity = 0;
  let existingAdditionQuantity = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const removalMatch = lines[i].match(removalPattern);
    const additionMatch = lines[i].match(additionPattern);
    
    if (removalMatch) {
      existingRemovalLineIndex = i;
      existingRemovalQuantity = parseInt(removalMatch[1]);
    }
    if (additionMatch) {
      existingAdditionLineIndex = i;
      existingAdditionQuantity = parseInt(additionMatch[1]);
    }
  }
  
  // Calculate quantity changes
  const totalRemoved = Math.max(0, originalQuantity - newQuantity);
  const totalAdded = Math.max(0, newQuantity - originalQuantity);
  
  // Handle removal notes
  if (totalRemoved > 0) {
    const newRemovalNote = newQuantity === 0 
      ? `x${originalQuantity} ${productName} removed (out of stock)`
      : `x${totalRemoved} ${productName} removed (out of stock)`;
    
    if (existingRemovalLineIndex >= 0) {
      lines[existingRemovalLineIndex] = newRemovalNote;
    } else {
      lines.push(newRemovalNote);
    }
    
    // Remove any existing addition notes since we're reducing
    if (existingAdditionLineIndex >= 0) {
      lines.splice(existingAdditionLineIndex, 1);
    }
  } else {
    // Remove existing removal notes if quantity is restored or increased
    if (existingRemovalLineIndex >= 0) {
      lines.splice(existingRemovalLineIndex, 1);
      // Adjust addition line index if it was after the removed line
      if (existingAdditionLineIndex > existingRemovalLineIndex) {
        existingAdditionLineIndex--;
      }
    }
  }
  
  // Handle addition notes
  if (totalAdded > 0) {
    const newAdditionNote = `x${totalAdded} ${productName} added`;
    
    if (existingAdditionLineIndex >= 0) {
      lines[existingAdditionLineIndex] = newAdditionNote;
    } else {
      lines.push(newAdditionNote);
    }
  } else {
    // Remove existing addition notes if quantity is back to original or reduced
    if (existingAdditionLineIndex >= 0) {
      lines.splice(existingAdditionLineIndex, 1);
    }
  }
  
  return lines.join('\n');
};

export function OrderSlip({ order, isOpen, onClose, highlightProductId, onSendToQuickBooks, isSendingToQB, readOnly = false, showItemThumbnails = false, onItemsChanged }: OrderSlipProps) {
  const [newItemProductId, setNewItemProductId] = useState<string>("");
  const [newItemQuantity, setNewItemQuantity] = useState<number>(1);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [comboboxValue, setComboboxValue] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState("");
  const [editingQuantity, setEditingQuantity] = useState<number | null>(null);
  const [editQuantityValue, setEditQuantityValue] = useState('');
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showCustomDateInput, setShowCustomDateInput] = useState(false);

  // Ref for auto-scrolling to highlighted product
  const highlightedItemRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Local state for the order being viewed — mutations update this directly in onSuccess
  // so the dialog re-renders immediately without depending on cache subscription timing.
  const [localOrder, setLocalOrder] = useState<any | null>(null);
  useEffect(() => {
    if (!isOpen || !order) { setLocalOrder(null); return; }
    const cached = (queryClient.getQueryData(['/api/orders']) as any[] || []).find((o: any) => o.id === order.id);
    setLocalOrder(cached || order);
  }, [isOpen, order?.id]);
  const currentOrder = localOrder;

  // Initialize notes value when order changes
  useEffect(() => {
    if (currentOrder) {
      setNotesValue(currentOrder.notes || "");
    }
  }, [currentOrder]);

  // Reset hasScrolled flag when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setHasScrolled(false);
    }
  }, [isOpen]);

  // Auto-scroll to highlighted product when order opens (only once per session)
  useEffect(() => {
    if (isOpen && highlightProductId && currentOrder?.items && !hasScrolled) {
      // Wait for items to be rendered, then scroll
      const scrollToHighlighted = () => {
        if (highlightedItemRef.current) {
          highlightedItemRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          setHasScrolled(true);
        }
      };
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        setTimeout(scrollToHighlighted, 150);
      });
    }
  }, [isOpen, highlightProductId, hasScrolled, currentOrder?.items?.length]);



  // Fetch order window config for auto-change-notes setting and fulfillment type options.
  // Use the resolved (account-aware) endpoint so per-account delivery/pickup overrides are respected.
  const { data: owConfig } = useQuery<any>({
    queryKey: order?.accountId
      ? ['/api/order-window-config/resolved', order.accountId]
      : ['/api/order-window-config'],
  });
  const autoChangeNotes = owConfig?.autoChangeNotes !== false;

  // The dates staff can move this order to: the last scheduled day before today, then
  // every scheduled day ahead — tomorrow included after the customers' cutoff — plus
  // the order's own date. "Custom date..." takes any other.
  const availableDates = useMemo(() => {
    if (!owConfig) return [];

    const fulfillmentType: 'delivery' | 'pickup' =
      (currentOrder as any)?.fulfillmentType === 'pickup' ? 'pickup' : 'delivery';

    const dates = getStaffFulfillmentDates(fulfillmentType, owConfig as WindowConfig, { maxDates: 10 })
      .map(({ value, label, sublabel }) => ({ value, label: `${label}, ${sublabel}` }));

    const currentFulfillmentDate = (currentOrder as any)?.fulfillmentDate || (currentOrder as any)?.pickupDate;
    if (currentFulfillmentDate && !dates.some((d) => d.value === currentFulfillmentDate)) {
      const { label, sublabel } = getFulfillmentDateOption(currentFulfillmentDate, owConfig as WindowConfig);
      dates.push({ value: currentFulfillmentDate, label: `${label}, ${sublabel} (current)` });
    }

    return dates;
  }, [owConfig, currentOrder]);

  const formatFulfillmentDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not set';
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (dateStr === todayStr) return 'Today';
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  // Fetch all products for adding new items
  const { data: products = [] } = useQuery<any[]>({
    queryKey: ['/api/products'],
    // Admin-only endpoint (DS-3) and only the add-item picker reads it, so a
    // read-only slip (the public demo recap) never asks.
    enabled: isOpen && !readOnly
  });

  // The vendor's delivery charge (a product, organizations.delivery_fee_product_id), or
  // null. It is never stored on the order: a delivery order shows it as a line here and
  // its invoice carries it (server/services/deliveryFee.ts). Kept out of Add Item so it
  // is never charged twice.
  // `exempt`: this account (its type, or its group) pays no delivery charge.
  // `chargedOnOrderId`: one fee per account per day — another order that day carries it.
  // Keyed under the account page's `/api/delivery-fee?accountId=` key, so its exemption
  // switch refreshes this too; the date and type make a move a new read, and staleTime 0
  // re-reads on every open (another order of the day may have been sent or cancelled).
  const deliveryFeePath = `/api/delivery-fee?accountId=${order?.accountId ?? ''}&orderId=${order?.id ?? ''}`;
  const { data: deliveryFee } = useQuery<{ productId: number; name: string; price: number | null; exempt: boolean; chargedOnOrderId: number | null } | null>({
    queryKey: [`/api/delivery-fee?accountId=${order?.accountId ?? ''}`, order?.id ?? null, currentOrder?.fulfillmentDate ?? null, currentOrder?.fulfillmentType ?? null],
    queryFn: readPath(deliveryFeePath),
    staleTime: 0,
    enabled: isOpen && Boolean(order?.accountId),
  });
  const deliveryFeeApplies = Boolean(deliveryFee && !deliveryFee.exempt && currentOrder && orderGetsDeliveryFee(currentOrder)
    && !currentOrder.items?.some((item: any) => item.product?.id === deliveryFee.productId));
  const showsDeliveryFee = deliveryFeeApplies && !deliveryFee?.chargedOnOrderId;

  // The order's account's customer type: a line whose typed price is cleared goes back to
  // the type's price (the server resolves it), so that is the price greyed in the box.
  const orderTypeId = currentOrder?.account?.customerTypeId ?? null;
  const { data: orderAccountType } = useQuery<CustomerTypeDetail>({
    queryKey: [customerTypePath(orderTypeId ?? 0)],
    enabled: isOpen && !readOnly && orderTypeId != null,
  });
  const accountType = orderTypeId != null ? orderAccountType : null;

  // Per-order action layer — owns setStatus/cancelOrder/printPickSheet mutations
  // with toasts + query invalidations. Send-to-QB stays delegated to the parent
  // via the existing onSendToQuickBooks prop (parent owns the multi-step
  // broken-link / unlinked-account / resend flow).
  const orderActions = useOrderActions({
    orderId: currentOrder?.id ?? 0,
    onSendToQuickBooks,
  });

  const updateNotesMutation = useMutation({
    mutationFn: async (notes: string) => {
      if (!currentOrder) throw new Error("No order selected");
      const res = await apiRequest('PATCH', `/api/orders/${currentOrder.id}/notes?suppressNotifications=true`, { notes });
      return await res.json();
    },
    onSuccess: (updatedOrder) => {
      setLocalOrder(updatedOrder);
      queryClient.setQueryData(['/api/orders'], (oldData: any) => {
        return oldData?.map((o: any) => o.id === currentOrder?.id ? updatedOrder : o) || [];
      });
      queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
      setEditingNotes(false);
      toast({
        title: "Success",
        description: "Order notes updated successfully"
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update order notes",
        variant: "destructive"
      });
    }
  });

  // A price typed for one line of this order (shared/linePrice.ts): PATCH { unitPrice };
  // "" or null puts the line back on the account's menu price.
  // The box saves when it loses focus — clicking away, opening another line's price,
  // closing the slip; Enter and "save" just blur it. Only Escape or "cancel" drop a typed
  // price (Pitt 2026-10-19: a row of prices typed without Enter never reached the server
  // and the invoice went at the menu's). `priceEditSettled` keeps one edit to one save.
  const [priceEditItemId, setPriceEditItemId] = useState<number | null>(null);
  const [priceDraft, setPriceDraft] = useState("");
  const priceEditSettled = useRef(false);
  const priceInputRef = useRef<HTMLInputElement>(null);
  const updateItemPriceMutation = useMutation({
    mutationFn: async ({ itemId, unitPrice }: { itemId: number; unitPrice: string | null }) => {
      if (!currentOrder) throw new Error("No order selected");
      const response = await apiRequest('PATCH', `/api/orders/${currentOrder.id}/items/${itemId}?suppressNotifications=true`, { unitPrice });
      return response.json();
    },
    onSuccess: (updatedOrder, { itemId }) => {
      setLocalOrder(updatedOrder);
      queryClient.setQueryData(['/api/orders'], (oldData: any) => oldData?.map((o: any) => (o.id === currentOrder?.id ? updatedOrder : o)) || []);
      queryClient.invalidateQueries({ queryKey: ['/api/orders'], refetchType: 'none' });
      // Close this line's box, not one opened on another line since.
      setPriceEditItemId(current => (current === itemId ? null : current));
    },
    onError: (error: any, { itemId }) => {
      toast({ title: "Price not saved", description: String(error?.message ?? error).replace(/^\d{3}:\s*/, ''), variant: "destructive" });
      // The box stays open with the price in it; leaving it again retries.
      setPriceEditItemId(current => {
        if (current === itemId) priceEditSettled.current = false;
        return current;
      });
    },
  });
  const startPriceEdit = (item: any) => {
    priceEditSettled.current = false;
    setPriceEditItemId(item.id);
    setPriceDraft(item.priceTyped && item.unitPrice ? item.unitPrice : '');
  };
  const cancelPriceEdit = () => {
    priceEditSettled.current = true;
    setPriceEditItemId(null);
  };
  const savePriceDraft = (item: any) => {
    if (priceEditSettled.current) return;
    const parsed = parseTypedPrice(priceDraft.trim());
    if ("error" in parsed) {
      toast({ title: "Check the price", description: parsed.error, variant: "destructive" });
      return;
    }
    priceEditSettled.current = true;
    const unitPrice = parsed.given ? parsed.price : null;
    const current = item.priceTyped && item.unitPrice ? item.unitPrice : null;
    if (unitPrice === current) {
      setPriceEditItemId(open => (open === item.id ? null : open));
      return;
    }
    updateItemPriceMutation.mutate({ itemId: item.id, unitPrice });
  };

  const updateItemMutation = useMutation({
    mutationFn: async ({ itemId, quantity, originalQuantity, productName }: { itemId: number; quantity: number; originalQuantity: number; productName: string }) => {
      if (!currentOrder) throw new Error("No order selected");
      
      // First, get the item's original ordered quantity from the order
      const item = currentOrder.items?.find((item: any) => item.id === itemId);
      const originalOrderedQuantity = item ? (item.originalQuantity ?? item.quantity) : originalQuantity;
      
      const response = await apiRequest('PATCH', `/api/orders/${currentOrder.id}/items/${itemId}?suppressNotifications=true`, { quantity });
      const updatedOrder = await response.json();
      
      if (autoChangeNotes) {
        // Use pre-mutation notes as baseline so server-written lines don't contaminate the result
        const baseNotes = currentOrder.notes || "";
        const updatedNotes = updateSmartNotes(baseNotes, productName, quantity, originalOrderedQuantity);

        if (updatedNotes !== baseNotes) {
          await apiRequest('PATCH', `/api/orders/${currentOrder.id}/notes?suppressNotifications=true`, { notes: updatedNotes });

          const finalResponse = await apiRequest('GET', `/api/orders/${currentOrder.id}`);
          const freshOrder = await finalResponse.json();
          return freshOrder;
        }
      }

      return updatedOrder;
    },
    onSuccess: (updatedOrder) => {
      setLocalOrder(updatedOrder);
      queryClient.setQueryData(['/api/orders'], (oldData: any) => {
        return oldData?.map((o: any) => o.id === currentOrder?.id ? updatedOrder : o) || [];
      });
      queryClient.invalidateQueries({ queryKey: ['/api/orders'], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'], refetchType: 'none' });
      onItemsChanged?.();
      toast({
        title: "Success",
        description: "Item quantity updated successfully"
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update item quantity",
        variant: "destructive"
      });
    }
  });

  const deleteItemMutation = useMutation({
    mutationFn: async ({ itemId, productName, quantity }: { itemId: number; productName: string; quantity: number }) => {
      if (!currentOrder) throw new Error("No order selected");
      
      // Get the item's original quantity
      const item = currentOrder.items?.find((item: any) => item.id === itemId);
      const originalOrderedQuantity = item ? (item.originalQuantity ?? item.quantity) : quantity;
      
      const response = await apiRequest('DELETE', `/api/orders/${currentOrder.id}/items/${itemId}`);
      const updatedOrder = await response.json();
      
      if (autoChangeNotes) {
        const baseNotes = currentOrder.notes || "";
        const updatedNotes = updateSmartNotes(baseNotes, productName, 0, originalOrderedQuantity);

        if (updatedNotes !== baseNotes) {
          await apiRequest('PATCH', `/api/orders/${currentOrder.id}/notes?suppressNotifications=true`, { notes: updatedNotes });

          const finalResponse = await apiRequest('GET', `/api/orders/${currentOrder.id}`);
          const freshOrder = await finalResponse.json();
          return freshOrder;
        }
      }

      return updatedOrder;
    },
    onSuccess: (updatedOrder) => {
      setLocalOrder(updatedOrder);
      queryClient.setQueryData(['/api/orders'], (oldData: any) => {
        return oldData?.map((o: any) => o.id === currentOrder?.id ? updatedOrder : o) || [];
      });
      queryClient.invalidateQueries({ queryKey: ['/api/orders'], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'], refetchType: 'none' });
      onItemsChanged?.();
      toast({
        title: "Success",
        description: "Item removed successfully"
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove item",
        variant: "destructive"
      });
    }
  });

  const addItemMutation = useMutation({
    mutationFn: async ({ productId, quantity, productName }: { productId: number; quantity: number; productName?: string }) => {
      if (!currentOrder) throw new Error("No order selected");
      
      // Add the item first
      const response = await apiRequest('POST', `/api/orders/${currentOrder.id}/items`, { productId, quantity });
      const updatedOrder = await response.json();
      
      if (autoChangeNotes) {
        const product = products.find((p: any) => p.id === productId);
        const itemName = productName || product?.name || "Unknown Product";

        const baseNotes = currentOrder.notes || "";
        const updatedNotes = updateSmartNotes(baseNotes, itemName, quantity, 0);

        if (updatedNotes !== baseNotes) {
          await apiRequest('PATCH', `/api/orders/${currentOrder.id}/notes?suppressNotifications=true`, { notes: updatedNotes });

          const finalResponse = await apiRequest('GET', `/api/orders/${currentOrder.id}`);
          const freshOrder = await finalResponse.json();
          return freshOrder;
        }
      }

      return updatedOrder;
    },
    onSuccess: (updatedOrder) => {
      setLocalOrder(updatedOrder);
      queryClient.setQueryData(['/api/orders'], (oldData: any) => {
        return oldData?.map((o: any) => o.id === currentOrder?.id ? updatedOrder : o) || [];
      });
      queryClient.invalidateQueries({ queryKey: ['/api/orders'], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'], refetchType: 'none' });
      onItemsChanged?.();
      setNewItemProductId("");
      setNewItemQuantity(1);
      toast({
        title: "Success",
        description: "Item added successfully"
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add item",
        variant: "destructive"
      });
    }
  });

  const updateFulfillmentMutation = useMutation({
    mutationFn: async ({ orderId, fulfillmentDate, fulfillmentType }: {
      orderId: number;
      fulfillmentDate?: string;
      fulfillmentType?: string;
    }) => {
      const body: Record<string, string> = {};
      if (fulfillmentDate) body.fulfillmentDate = fulfillmentDate;
      if (fulfillmentType) body.fulfillmentType = fulfillmentType;
      return apiRequest('PATCH', `/api/orders/${orderId}/fulfillment`, body);
    },
    onSuccess: (_, variables) => {
      const { orderId, fulfillmentDate, fulfillmentType } = variables;
      const applyPatch = (o: any) => {
        const patch: Record<string, string> = {};
        if (fulfillmentDate !== undefined) { patch.fulfillmentDate = fulfillmentDate; patch.pickupDate = fulfillmentDate; }
        if (fulfillmentType !== undefined) { patch.fulfillmentType = fulfillmentType; }
        return { ...o, ...patch };
      };
      setLocalOrder((prev: any) => prev?.id === orderId ? applyPatch(prev) : prev);
      queryClient.setQueryData(['/api/orders'], (oldData: any) => {
        if (!Array.isArray(oldData)) return oldData;
        return oldData.map((o: any) => o.id !== orderId ? o : applyPatch(o));
      });
      queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      toast({ title: "Order updated", description: "Fulfillment details updated successfully." });
    },
    onError: () => {
      toast({ title: "Failed to update order", description: "Please try again.", variant: "destructive" });
    },
  });

  const handleFulfillmentDateChange = (orderId: number, newDate: string) => {
    updateFulfillmentMutation.mutate({ orderId, fulfillmentDate: newDate });
  };

  const handleFulfillmentTypeChange = (orderId: number, newType: string) => {
    updateFulfillmentMutation.mutate({ orderId, fulfillmentType: newType });
  };

  const handleStatusUpdate = (newStatus: "pending" | "reviewed" | "cancelled") => {
    orderActions.setStatus(newStatus);
  };

  const handleInstantQuantityChange = (itemId: number, newQuantity: number) => {
    if (newQuantity < 0) return;

    // Find the item to get original quantity and product name
    const item = currentOrder?.items.find((i: any) => i.id === itemId);
    if (item) {
      // Quantity reduced to 0 — delete the item entirely
      if (newQuantity === 0) {
        deleteItemMutation.mutate({
          itemId,
          productName: item.product.name,
          quantity: item.quantity
        });
        return;
      }

      // Use the stored originalQuantity if available, otherwise use current quantity
      // For newly added items, originalQuantity should be 0
      const originalQuantity = item.originalQuantity !== undefined ? item.originalQuantity : item.quantity;

      updateItemMutation.mutate({
        itemId,
        quantity: newQuantity,
        originalQuantity: originalQuantity,
        productName: item.product.name
      });
    }
  };



  const handleAddItem = () => {
    if (newItemProductId && newItemQuantity > 0) {
      const product = products.find((p: any) => p.id === parseInt(newItemProductId));
      addItemMutation.mutate({ 
        productId: parseInt(newItemProductId), 
        quantity: newItemQuantity,
        productName: product?.name
      });
    }
  };

  // Auto-add item when quantity is changed for new items
  const handleNewItemQuantityChange = (newQuantity: number) => {
    if (newQuantity <= 0) {
      setNewItemQuantity(1);
      return;
    }
    
    setNewItemQuantity(newQuantity);
    
    // Auto-add item if product is selected
    if (newItemProductId) {
      const product = products.find((p: any) => p.id === parseInt(newItemProductId));
      addItemMutation.mutate({ 
        productId: parseInt(newItemProductId), 
        quantity: newQuantity,
        productName: product?.name
      });
    }
  };

  const handleNotesEdit = () => {
    setEditingNotes(true);
  };

  const handleNotesSave = () => {
    updateNotesMutation.mutate(notesValue);
  };

  const handleNotesCancel = () => {
    setNotesValue(currentOrder?.notes || "");
    setEditingNotes(false);
  };

  // Quantity editing handlers
  const handleQuantityClick = (currentValue: number, itemId: number) => {
    setEditingQuantity(itemId);
    setEditQuantityValue(currentValue.toString());
  };

  const handleQuantityInputSave = (itemId: number) => {
    const newValue = parseInt(editQuantityValue) || 0;
    const clampedValue = Math.max(0, newValue);
    handleInstantQuantityChange(itemId, clampedValue);
    setEditingQuantity(null);
    setEditQuantityValue('');
  };

  const handleQuantityInputKeyDown = (e: React.KeyboardEvent, itemId: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleQuantityInputSave(itemId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingQuantity(null);
      setEditQuantityValue('');
    }
  };

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'reviewed':
        return 'Reviewed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'reviewed':
        return <Package className="h-4 w-4" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-blue-200 text-cn-brand-blue dark:bg-blue-900 dark:text-blue-300';
      case 'reviewed':
        return 'bg-orange-100 text-[#ea580b] dark:bg-orange-900 dark:text-orange-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  // Phase 1: cycle reduced to pending → reviewed (and reviewed/cancelled → pending fallback).
  // Phase 2 will narrow this further and own the order-slip bottom-buttons redesign.
  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case 'pending':
        return 'reviewed';
      case 'reviewed':
        return 'pending';
      case 'cancelled':
        return 'pending';
      default:
        return 'pending';
    }
  };

  const getButtonText = (currentStatus: string) => {
    switch (currentStatus) {
      case 'pending':
        return 'Mark as Reviewed';
      case 'cancelled':
        return 'Restore Order';
      default:
        return 'Mark as Reviewed';
    }
  };

  if (!currentOrder) return null;

  // Enhanced close handler to prevent event propagation
  const handleDialogChange = (open: boolean) => {
    if (!open) {
      // Refetch stale queries now that the dialog is closing
      queryClient.refetchQueries({ queryKey: ['/api/orders'] });
      queryClient.refetchQueries({ queryKey: ['/api/dashboard/stats'] });
      setTimeout(() => onClose(), 0);
    }
  };

  // Shared header content used in both Dialog and read-only page modes
  const orderHeader = (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <h2 className="text-lg sm:text-xl font-medium text-dark-grey break-words">
        Order #ORD-{(currentOrder.id ?? 0).toString().padStart(3, '0')}
      </h2>
      <div className="flex flex-nowrap items-center gap-2 self-start sm:self-center flex-shrink-0">
        <span
          className={`${
            currentOrder.status === "reviewed" ? "bg-orange-100 text-[#ea580b]" :
            currentOrder.status === "cancelled" ? "bg-red-100 text-red-800" :
            "bg-blue-200 text-cn-brand-blue"
          } px-2 py-1 rounded text-xs cursor-default`}
        >
          {currentOrder.status}
        </span>
        <QbSyncedMark syncedAt={(currentOrder as any).quickbooksSyncedAt} size={18} className="flex-shrink-0" />
        {/* An order Clyde placed: one bubble, "View conversation" — hover previews the
            summary that became the order, click opens the thread on it. The read-only
            slip (a customer's, the demo's) shows the plain badge. */}
        <OrderSourceMark
          order={currentOrder}
          label="View conversation"
          onNavigate={onClose}
          plain={readOnly}
          size={14}
          className="flex-shrink-0"
        />
      </div>
    </div>
  );

  // Shared body content (editing controls conditionally hidden when readOnly)
  const orderBody = (
    <div className="space-y-6">
        
        <div className="space-y-6">
          <Card>
            <CardContent className="space-y-3 pt-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                <span className="text-sm font-medium text-gray-600">Customer:</span>
                <span className="text-sm font-medium text-dark-grey break-words">{currentOrder.account.name}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                <span className="text-sm font-medium text-gray-600">Order Date:</span>
                <span className="text-sm text-dark-grey break-words">
                  {new Date(currentOrder.createdAt).toLocaleDateString()}
                </span>
              </div>
              {(currentOrder.fulfillmentDate || currentOrder.pickupDate) && (
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                  <span className="text-sm font-medium text-gray-600">
                    {(currentOrder as any).fulfillmentType === 'pickup' ? 'Pickup Day:' : 'Delivery Date:'}
                  </span>
                  {readOnly ? (
                    <span className="text-sm font-semibold text-dark-grey">
                      {formatFulfillmentDate(currentOrder.fulfillmentDate || currentOrder.pickupDate)}
                    </span>
                  ) : (
                    <Popover onOpenChange={(open) => { if (!open) setShowCustomDateInput(false); }}>
                      <PopoverTrigger asChild>
                        <button className="text-sm font-semibold text-cn-brand-blue hover:underline cursor-pointer flex items-center gap-1">
                          {formatFulfillmentDate(currentOrder.fulfillmentDate || currentOrder.pickupDate)}
                          <Pencil className="h-3 w-3 opacity-50" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-2" align="end">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-500 px-2 pb-1">Change date</p>
                          {availableDates.map(({ value, label }) => (
                            <Button
                              key={value}
                              variant="ghost"
                              size="sm"
                              className={`w-full justify-start text-xs ${
                                value === (currentOrder.fulfillmentDate || currentOrder.pickupDate)
                                  ? 'bg-blue-50 text-cn-brand-blue font-semibold'
                                  : ''
                              }`}
                              onClick={() => handleFulfillmentDateChange(currentOrder.id, value)}
                              disabled={value === (currentOrder.fulfillmentDate || currentOrder.pickupDate) || updateFulfillmentMutation.isPending}
                            >
                              {label}
                            </Button>
                          ))}
                          {!showCustomDateInput ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-xs text-gray-500"
                              onClick={() => setShowCustomDateInput(true)}
                              disabled={updateFulfillmentMutation.isPending}
                            >
                              Custom date...
                            </Button>
                          ) : (
                            <input
                              type="date"
                              className="w-full text-xs border border-gray-200 rounded px-2 py-1 mt-1"
                              autoFocus
                              defaultValue={currentOrder.fulfillmentDate || currentOrder.pickupDate || ""}
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleFulfillmentDateChange(currentOrder.id, e.target.value);
                                  setShowCustomDateInput(false);
                                }
                              }}
                            />
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}
              {owConfig?.deliveryEnabled && owConfig?.pickupEnabled && (currentOrder as any).fulfillmentType && (
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                  <span className="text-sm font-medium text-gray-600">Fulfillment:</span>
                  {readOnly ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                      (currentOrder as any).fulfillmentType === 'delivery'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {(currentOrder as any).fulfillmentType === 'delivery' ? (
                        <><Truck className="h-3 w-3" /> Delivery</>
                      ) : (
                        <><MapPin className="h-3 w-3" /> Pickup</>
                      )}
                    </span>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold cursor-pointer hover:opacity-80 ${
                          (currentOrder as any).fulfillmentType === 'delivery'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {(currentOrder as any).fulfillmentType === 'delivery' ? (
                            <><Truck className="h-3 w-3" /> Delivery</>
                          ) : (
                            <><MapPin className="h-3 w-3" /> Pickup</>
                          )}
                          <Pencil className="h-2.5 w-2.5 opacity-50" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-40 p-2" align="end">
                        <div className="space-y-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-xs"
                            onClick={() => handleFulfillmentTypeChange(currentOrder.id, 'delivery')}
                            disabled={(currentOrder as any).fulfillmentType === 'delivery' || updateFulfillmentMutation.isPending}
                          >
                            <Truck className="mr-2 h-3 w-3" /> Delivery
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-xs"
                            onClick={() => handleFulfillmentTypeChange(currentOrder.id, 'pickup')}
                            disabled={(currentOrder as any).fulfillmentType === 'pickup' || updateFulfillmentMutation.isPending}
                          >
                            <MapPin className="mr-2 h-3 w-3" /> Pickup
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                <span className="text-sm font-medium text-gray-600">Total Items:</span>
                <span className="text-sm font-medium text-dark-grey">{currentOrder.totalItems}</span>
              </div>
              {/* Order total — only when every line on the order has a price */}
              {(() => {
                const totals = orderTotals((currentOrder.items || []).map((item: any) => ({
                  quantity: item.quantity,
                  price: item.unitPrice,
                  taxPercent: item.taxPercent,
                })).concat(showsDeliveryFee && deliveryFee ? [{ quantity: 1, price: deliveryFee.price, taxPercent: null }] : []));
                if (!totals) return null;
                return (
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center pt-2 border-t" data-testid="order-slip-total">
                    <span className="text-sm font-medium text-gray-600">Est. Total:</span>
                    <span className="text-sm font-bold text-cn-brand-green">
                      ${totals.total.toFixed(2)}
                      {totals.tax > 0 && <span className="text-xs font-normal text-gray-500 ml-1">(inc. ${totals.tax.toFixed(2)} tax)</span>}
                    </span>
                  </div>
                );
              })()}
              {currentOrder.isKioskOrder && (
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                  <span className="text-sm font-medium text-gray-600">Delivery Location:</span>
                  <span className="text-sm font-medium text-dark-grey" data-testid="display-delivery-location">
                    {currentOrder.deliveryLocation || "Not specified"}
                  </span>
                </div>
              )}
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-600">Order Notes:</span>
                {!readOnly && editingNotes ? (
                  <div className="space-y-2">
                    <Textarea
                      value={notesValue}
                      onChange={(e) => setNotesValue(e.target.value)}
                      placeholder=""
                      className="min-h-[100px] resize-none"
                      autoFocus
                      data-testid="textarea-order-notes"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleNotesSave}
                        disabled={updateNotesMutation.isPending}
                        className="bg-cn-green hover:bg-cn-green/90 text-white text-xs px-2"
                        data-testid="button-save-notes"
                      >
                        <CheckCircle className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleNotesCancel}
                        disabled={updateNotesMutation.isPending}
                        className="text-xs px-2 text-red-600 hover:text-red-700"
                        data-testid="button-cancel-notes"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`text-sm text-dark-grey bg-gray-50 dark:bg-gray-800 p-3 rounded-md whitespace-pre-wrap min-h-[60px] transition-colors ${
                      !readOnly ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''
                    }`}
                    onClick={!readOnly ? handleNotesEdit : undefined}
                    data-testid="display-order-notes"
                  >
                    {currentOrder.notes || "..."}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Order Items */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Order Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {currentOrder.items
                  .sort((a: any, b: any) => a.id - b.id) // Ensure stable client-side ordering by item ID
                  .map((item: any) => {
                    // Check if this item should be highlighted
                    const isHighlighted = highlightProductId && item.product.id === highlightProductId;
                  return (
                    <div
                      key={`item-${item.id}`}
                      ref={isHighlighted ? highlightedItemRef : null}
                      className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-3 border rounded-lg transition-colors ${
                        isHighlighted ? 'bg-blue-50 border-blue-300 shadow-sm' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-dark-grey break-words">
                          {item.product.name}
                          {readOnly ? (
                            item.unitPrice && (
                              <span className="text-sm font-normal text-gray-500 ml-2">
                                ${item.unitPrice}{item.taxPercent && parseFloat(item.taxPercent) > 0 ? ' +tax' : ''}
                              </span>
                            )
                          ) : priceEditItemId === item.id ? (
                            // Typing a price for this order's line; leaving the box saves it
                            // (Enter and "save" blur it), Escape or "cancel" drop it.
                            <span className="ml-2 inline-flex items-center gap-1 text-sm font-normal text-gray-600">
                              $
                              <input
                                ref={priceInputRef}
                                autoFocus
                                type="text"
                                inputMode="decimal"
                                value={priceDraft}
                                placeholder={accountMenuPrice(item.product, accountType) ?? ''}
                                onChange={(e) => setPriceDraft(e.target.value)}
                                onBlur={() => savePriceDraft(item)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                                  if (e.key === 'Escape') cancelPriceEdit();
                                }}
                                className="w-20 h-7 px-1 border rounded text-sm"
                                data-testid={`input-line-price-${item.id}`}
                              />
                              {/* mousedown keeps focus in the box, so the click decides: save blurs it, cancel drops it */}
                              <button type="button" className="text-xs underline" onMouseDown={(e) => e.preventDefault()} onClick={() => priceInputRef.current?.blur()} disabled={updateItemPriceMutation.isPending}>save</button>
                              <button type="button" className="text-xs underline text-gray-400" onMouseDown={(e) => e.preventDefault()} onClick={cancelPriceEdit}>cancel</button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => startPriceEdit(item)}
                              className={`ml-2 text-sm font-normal underline decoration-dotted underline-offset-2 ${item.priceTyped ? 'text-cn-brand-green' : 'text-gray-500'}`}
                              title={item.priceTyped ? "Price set for this order — change it, or clear it for the menu price" : "Set a price for this order"}
                              data-testid={`button-line-price-${item.id}`}
                            >
                              {item.unitPrice ? `$${item.unitPrice}${item.taxPercent && parseFloat(item.taxPercent) > 0 ? ' +tax' : ''}` : 'set price'}
                              {item.priceTyped && <span className="ml-1 text-xs">(this order)</span>}
                            </button>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {readOnly ? (
                          <>
                            <span className="font-medium text-lg min-w-[3rem] text-center text-dark-grey px-2" data-testid={`text-quantity-${item.id}`}>
                              {item.quantity}
                            </span>
                            {showItemThumbnails && (
                              // Hardcoded to demo-public because showItemThumbnails is only ever true in
                              // the demo admin view context. If this prop is ever reused elsewhere, the
                              // caller should pass the org slug as a separate prop rather than deriving it.
                              <img
                                src={`/org/demo-public/api/products/${item.product.id}/image`}
                                alt={item.product.name}
                                className="w-12 h-12 rounded object-cover flex-shrink-0 bg-gray-100"
                                onError={(e) => {
                                  const target = e.currentTarget;
                                  target.style.display = 'none';
                                  const placeholder = target.nextElementSibling as HTMLElement | null;
                                  if (placeholder) placeholder.style.display = 'flex';
                                }}
                              />
                            )}
                            {showItemThumbnails && (
                              <div
                                className="w-12 h-12 rounded bg-gray-100 flex-shrink-0 items-center justify-center hidden"
                                aria-hidden="true"
                              >
                                <Package className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <Button
                              type="button"
                              onClick={() => handleInstantQuantityChange(item.id, Math.max(0, item.quantity - 1))}
                              className="bg-[#1D3D88] hover:bg-[#1D3D88]/90 text-white rounded-lg p-3 min-w-[48px] h-12"
                              disabled={updateItemMutation.isPending}
                              data-testid={`button-minus-${item.id}`}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            {editingQuantity === item.id ? (
                              <Input
                                type="number"
                                value={editQuantityValue}
                                onChange={(e) => setEditQuantityValue(e.target.value)}
                                onBlur={() => handleQuantityInputSave(item.id)}
                                onKeyDown={(e) => handleQuantityInputKeyDown(e, item.id)}
                                className="font-medium text-lg min-w-[3rem] text-center h-12 p-2 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                                autoFocus
                                data-testid={`input-quantity-${item.id}`}
                              />
                            ) : (
                              <span
                                className="font-medium text-lg min-w-[3rem] text-center text-dark-grey cursor-pointer hover:bg-gray-100 rounded p-2 transition-colors"
                                onClick={() => handleQuantityClick(item.quantity, item.id)}
                                data-testid={`text-quantity-${item.id}`}
                              >
                                {item.quantity}
                              </span>
                            )}
                            <Button
                              type="button"
                              onClick={() => handleInstantQuantityChange(item.id, item.quantity + 1)}
                              className="bg-[#1D3D88] hover:bg-[#1D3D88]/90 text-white rounded-lg p-3 min-w-[48px] h-12"
                              disabled={updateItemMutation.isPending}
                              data-testid={`button-plus-${item.id}`}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {showsDeliveryFee && deliveryFee && (
                  <div
                    className="flex justify-between items-center gap-2 p-3 border rounded-lg bg-gray-50"
                    data-testid="order-slip-delivery-fee"
                  >
                    <span className="font-medium text-dark-grey">1 × {deliveryFee.name}</span>
                    <span className="text-sm text-gray-600">
                      {formatDeliveryFeePrice(deliveryFee.price) || 'No price set'} · once per delivery day
                    </span>
                  </div>
                )}
                {deliveryFeeApplies && deliveryFee?.chargedOnOrderId && (
                  <p className="text-sm text-gray-600 px-1" data-testid="order-slip-delivery-fee-elsewhere">
                    {deliveryFee.name} for this day is on order #{deliveryFee.chargedOnOrderId}.
                  </p>
                )}

                {/* Add New Item */}
                {!readOnly && <div className="flex flex-col sm:flex-row gap-2 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={comboboxOpen}
                        className="flex-1 justify-between h-auto min-h-[2.5rem] text-left font-normal"
                        data-testid="button-add-item-combobox"
                      >
                        {comboboxValue ? 
                          products.find((product: any) => product.id.toString() === comboboxValue)?.name :
                          "Add Item..."
                        }
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search products..." className="h-9" />
                        <CommandEmpty>No products found.</CommandEmpty>
                        <CommandList className="max-h-[300px]">
                          <CommandGroup>
                            {products
                              .filter((product: any) => !currentOrder.items.some((item: any) => item.product.id === product.id))
                              .filter((product: any) => product.id !== deliveryFee?.productId)
                              .sort((a: any, b: any) => a.name.localeCompare(b.name))
                              .map((product: any) => (
                                <CommandItem
                                  key={product.id}
                                  // cmdk filters on `value` — append active aliases so
                                  // "romas" surfaces "Roma Tomatoes 25lb" here too.
                                  value={[product.name, ...activeAliases(product.aliases)].join(" ")}
                                  onSelect={() => {
                                    const productId = product.id.toString();
                                    setComboboxValue(productId);
                                    setNewItemProductId(productId);
                                    setComboboxOpen(false);
                                    // Auto-add item when product is selected
                                    addItemMutation.mutate({ 
                                      productId: parseInt(productId), 
                                      quantity: newItemQuantity,
                                      productName: product.name
                                    });
                                    // Reset for next selection
                                    setTimeout(() => {
                                      setComboboxValue("");
                                      setNewItemProductId("");
                                    }, 100);
                                  }}
                                  data-testid={`option-product-${product.id}`}
                                >
                                  {product.name}
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {newItemProductId && (
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <Button
                        type="button"
                        onClick={() => handleNewItemQuantityChange(Math.max(1, newItemQuantity - 1))}
                        className="bg-[#1D3D88] hover:bg-[#1D3D88]/90 text-white rounded-lg p-3 min-w-[48px] h-12"
                        disabled={addItemMutation.isPending}
                        data-testid="button-minus-new"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span 
                        className="font-medium text-lg min-w-[3rem] text-center text-dark-grey cursor-pointer hover:bg-gray-100 rounded p-2 transition-colors" 
                        onClick={() => {
                          const input = prompt('Enter quantity:', newItemQuantity.toString());
                          if (input !== null) {
                            const newValue = parseInt(input) || 1;
                            const clampedValue = Math.max(1, newValue);
                            handleNewItemQuantityChange(clampedValue);
                          }
                        }}
                        data-testid="text-quantity-new"
                      >
                        {newItemQuantity}
                      </span>
                      <Button
                        type="button"
                        onClick={() => handleNewItemQuantityChange(newItemQuantity + 1)}
                        className="bg-[#1D3D88] hover:bg-[#1D3D88]/90 text-white rounded-lg p-3 min-w-[48px] h-12"
                        disabled={addItemMutation.isPending}
                        data-testid="button-plus-new"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>}
              </div>
            </CardContent>
          </Card>

          {/* Status Actions — two-row layout: status toggle + Send to QB, then Print Pick Sheet.
              Cancel Order lives in the row context menu / per-row ... button (Phase 3b). */}
          {!readOnly && <Card>
            <CardHeader className="pb-3">
            </CardHeader>
            <CardContent>
              {/* Row 1: status toggle + Send to QuickBooks */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => handleStatusUpdate(getNextStatus(currentOrder.status) as "pending" | "reviewed" | "cancelled")}
                  disabled={orderActions.isSettingStatus}
                  className={`flex-1 ${
                    currentOrder.status === 'pending' ? 'bg-orange-600 text-white hover:bg-orange-100 hover:text-orange-600' :
                    currentOrder.status === 'reviewed' ? 'bg-cn-brand-green text-white hover:bg-[var(--status-procured-bg)] hover:text-cn-brand-green' :
                    ''
                  }`}
                >
                  {orderActions.isSettingStatus ? (
                    "Updating..."
                  ) : currentOrder.status === 'pending' ? (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Mark as Reviewed
                    </>
                  ) : currentOrder.status === 'reviewed' ? (
                    <>
                      <Clock className="mr-2 h-4 w-4" />
                      Mark as Pending
                    </>
                  ) : (
                    getButtonText(currentOrder.status)
                  )}
                </Button>
                {/* QB-26: a cancelled order is never sendable — the server refuses it too. */}
                {onSendToQuickBooks && currentOrder.status !== 'cancelled' && (() => {
                  const isSynced = (currentOrder as any).quickbooksSyncedAt != null;
                  return (
                    <Button
                      onClick={() => onSendToQuickBooks(currentOrder.id)}
                      disabled={isSendingToQB}
                      className={`group flex-1 ${
                        isSendingToQB
                          ? 'bg-cn-brand-green text-white'
                          : isSynced
                            ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            : 'bg-cn-brand-green text-white hover:bg-[var(--status-procured-bg)] hover:text-cn-brand-green'
                      }`}
                    >
                      {isSendingToQB ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : isSynced ? (
                        <>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          <span className="group-hover:hidden">Sent to QuickBooks</span>
                          <span className="hidden group-hover:inline">Resend to QuickBooks</span>
                        </>
                      ) : (
                        <>
                          <FileText className="mr-2 h-4 w-4" />
                          Send to QuickBooks
                        </>
                      )}
                    </Button>
                  );
                })()}
              </div>

              {/* Row 2: Print Pick Sheet (full-width) */}
              <div className="mt-3">
                <Button
                  onClick={() => orderActions.printPickSheet()}
                  disabled={orderActions.isPrintingPickSheet}
                  className="w-full bg-[#1D3D88] hover:bg-[#1D3D88]/90 text-white"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Print Pick Sheet
                </Button>
              </div>

              {(currentOrder as any).quickbooksSyncedAt != null && (
                <div className="mt-3 p-3 bg-green-50 border border-cn-green rounded-md">
                  <p className="text-sm text-cn-green flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4" />
                    This order has been sent to QuickBooks successfully.
                  </p>
                </div>
              )}
              {currentOrder.status === 'cancelled' && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700 flex items-center">
                    <XCircle className="mr-2 h-4 w-4" />
                    This order has been cancelled.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>}
        </div>
      </div>
  ); // end orderBody

  // ── Read-only mode: bare render, no Dialog wrapper ─────────────────────────
  if (readOnly) {
    return (
      <div className="space-y-4">
        {orderHeader}
        {orderBody}
      </div>
    );
  }

  // ── Normal mode: Dialog wrapper ─────────────────────────────────────────────
  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent
        className="!w-[90vw] !max-w-sm sm:!max-w-2xl md:!max-w-3xl !max-h-[90vh] overflow-y-auto mx-auto p-4 sm:p-6 shadow-lg"
        aria-describedby="order-slip-description"
        onPointerDownOutside={(e) => e.stopPropagation()}
        onInteractOutside={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="sr-only">
            Order #ORD-{(currentOrder.id ?? 0).toString().padStart(3, '0')}
          </DialogTitle>
          {orderHeader}
        </DialogHeader>
        <div id="order-slip-description" className="sr-only">
          Order details and status management for order {currentOrder.id}
        </div>
        {orderBody}
      </DialogContent>
    </Dialog>
  );
}